import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ProductionOrderService from '../services/ProductionOrderService';

const initialState = {
    datas: [],
    pagination: {},
    data: null,
    loading: false,
    error: null,
    isSuccess: false,
    submitLoading: false,
};

export const fetchProductionOrders = createAsyncThunk(
    'productionOrder/fetchAll',
    async (params, { rejectWithValue }) => {
        try {
            const resp = await ProductionOrderService.List(params);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchProductionOrderById = createAsyncThunk(
    'productionOrder/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            const resp = await ProductionOrderService.Show(id);
            return resp.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createProductionOrder = createAsyncThunk(
    'productionOrder/create',
    async (data, { rejectWithValue }) => {
        try {
            const resp = await ProductionOrderService.Create(data);
            return resp.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateProductionOrder = createAsyncThunk(
    'productionOrder/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const resp = await ProductionOrderService.Update(id, data);
            return resp.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteProductionOrder = createAsyncThunk(
    'productionOrder/delete',
    async (id, { rejectWithValue }) => {
        try {
            const resp = await ProductionOrderService.Delete(id);
            return { id, message: resp.data?.message };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const productionOrderSlice = createSlice({
    name: 'productionOrder',
    initialState,
    reducers: {
        resetState: (state) => {
            state.data = null;
            state.loading = false;
            state.error = null;
            state.isSuccess = false;
            state.submitLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch All
            .addCase(fetchProductionOrders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductionOrders.fulfilled, (state, action) => {
                state.loading = false;

                state.datas = action.payload.data.data.productionOrderList;
                state.pagination = {
                    total: action.payload.data.data.pagination.total,
                    current_page: action.payload.data.data.pagination.current_page,
                    per_page: action.payload.data.data.pagination.per_page,
                    last_page: action.payload.data.data.pagination.last_page,
                };
            })
            .addCase(fetchProductionOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch By Id
            .addCase(fetchProductionOrderById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductionOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.data;
            })
            .addCase(fetchProductionOrderById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create
            .addCase(createProductionOrder.pending, (state) => {
                state.submitLoading = true;
                state.isSuccess = false;
            })
            .addCase(createProductionOrder.fulfilled, (state, action) => {
                state.submitLoading = false;
                state.isSuccess = true;
                state.data = action.payload.data;
                toast.success(action.payload?.message || 'Production Order created successfully');
            })
            .addCase(createProductionOrder.rejected, (state, action) => {
                state.submitLoading = false;
                state.error = action.payload;
                toast.error(action.payload?.message || 'Failed to create Production Order');
            })
            // Update
            .addCase(updateProductionOrder.pending, (state) => {
                state.submitLoading = true;
                state.isSuccess = false;
            })
            .addCase(updateProductionOrder.fulfilled, (state, action) => {
                state.submitLoading = false;
                state.isSuccess = true;
                toast.success(action.payload?.message || 'Production Order updated successfully');
            })
            .addCase(updateProductionOrder.rejected, (state, action) => {
                state.submitLoading = false;
                state.error = action.payload;
                toast.error(action.payload?.message || 'Failed to update Production Order');
            })
            // Delete
            .addCase(deleteProductionOrder.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(deleteProductionOrder.fulfilled, (state, action) => {
                state.submitLoading = false;
                state.datas = state.datas.filter(item => item.id !== action.payload.id);
                toast.success(action.payload?.message || 'Production Order deleted successfully');
            })
            .addCase(deleteProductionOrder.rejected, (state, action) => {
                state.submitLoading = false;
                toast.error(action.payload?.message || 'Failed to delete Production Order');
            });
    },
});

export const { resetState } = productionOrderSlice.actions;
export const productionOrderSelector = (state) => state.productionOrder;
export default productionOrderSlice.reducer;
